import { useCollectionHandle } from '@portal/state/hooks/useCollectionHandle';
import { useV53AccountIdentifier } from '@portal/state/hooks/useV53AccountIdentifier';
import { useQueryGetShopifyCollectionProducts } from 'processes/SubscriptionPageV2/hooks/productsByCollections';
import { useMemo } from 'react';
import { useQueryGetShopifyCartOneTimeProductsV2 } from 'state/cart';
import { useStoreValue } from 'state/hooks/useStore';
import { useSiteCart } from '../../SiteCart/hooks/SiteCartContext';

type UseCartModalCarouselProductsV2 = {
  showScoopFirstInCarousel?: boolean;
};

export const useCartModalCarouselProductsV2 = ({
  showScoopFirstInCarousel = false
}: UseCartModalCarouselProductsV2) => {
  const store = useStoreValue();
  const v53Enabled = useV53AccountIdentifier();

  const { cart, setCart } = useQueryGetShopifyCartOneTimeProductsV2();
  const { otpItems: otpCartItems } = useSiteCart();
  const { collectionHandle } = useCollectionHandle();

  const { data: oneTimeProducts = [], isPending: oneTimeProductsIsLoading } =
    useQueryGetShopifyCollectionProducts({
      storeCode: store,
      collectionHandle,
      identifiers: [{ key: 'carousel_visible', namespace: 'custom' }]
    });

  const sortedOneTimeProducts = useMemo(() => {
    if (!oneTimeProducts || oneTimeProducts.length === 0) {
      return [];
    }

    const carouselProducts = oneTimeProducts.filter(
      (product) =>
        !!product?.metafields?.find(
          (metafield) => metafield?.key === 'carousel_visible' && metafield?.value === 'true'
        )
    );

    if (!showScoopFirstInCarousel) {
      return carouselProducts;
    }

    return carouselProducts.sort((a, b) => {
      if (a.title.toLowerCase().includes('scoop')) {
        return -1;
      }

      if (b.title.toLowerCase().includes('scoop')) {
        return 1;
      }

      return 0;
    });
  }, [oneTimeProducts, showScoopFirstInCarousel]);

  const targetProducts = useMemo(() => {
    if (!sortedOneTimeProducts || sortedOneTimeProducts.length === 0) {
      return [];
    }

    if (otpCartItems.length === 0) {
      return sortedOneTimeProducts;
    }

    return sortedOneTimeProducts.filter(
      (product) =>
        !otpCartItems.some((cartProduct) =>
          product.variants.find((variantId) => variantId.id === cartProduct.merchandise.id)
        )
    );
  }, [otpCartItems, sortedOneTimeProducts]);

  const productsWithoutAGZ = targetProducts.filter(
    (product) => !product?.title?.toLowerCase()?.includes('agz')
  );

  return {
    // AGZ products should not yet be included in cart carousel
    products: v53Enabled ? productsWithoutAGZ : targetProducts,
    productsIsLoading: oneTimeProductsIsLoading,
    cart,
    setCart
  };
};
